/**
 * "Manually reset password" for a tenant's portal login. Rendered inside
 * `PortalAccessCard` for `resource: 'tenants'` only, and only for ADMIN/
 * DEVELOPER (`tenants:reset-password`). The server is the real gate: the
 * capability is re-checked on both routes, and the 4-per-month cap lives in
 * `manuallyResetTenantPassword` (modules/tenants/service.ts), not here. The
 * disabled button at 0 remaining is a courtesy, not a guard.
 *
 * The generated password lives in component state only: never in a query
 * cache, never in storage. It's cleared on "Done" and gone on unmount, so
 * leaving the page is the same as dismissing it.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';

interface ManualPasswordResetStatus {
  limit: number;
  usedThisMonth: number;
  remaining: number;
  resetsOn: string;
  lastResetAt: string | null;
  lastResetByName: string | null;
}

interface ManualPasswordResetResult {
  password: string;
  status: ManualPasswordResetStatus;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/Detroit',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Detroit',
  });
}

export function ManualPasswordReset({ tenantId }: { tenantId: string }) {
  const [status, setStatus] = useState<ManualPasswordResetStatus | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await api<ManualPasswordResetStatus>(`/tenants/${tenantId}/manual-password-reset`));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load reset history.');
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function reset() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<ManualPasswordResetResult>(
        `/tenants/${tenantId}/manual-password-reset`,
        { method: 'POST' },
      );
      setPassword(result.password);
      setStatus(result.status);
      setCopied(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not reset the password.');
      // The count may have moved under us (another staff member, another tab).
      void load();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  async function copy() {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      setError('Copy failed. Select the password and copy it manually.');
    }
  }

  const exhausted = status !== null && status.remaining === 0;

  return (
    <div className="stack" style={{ gap: '0.5rem' }}>
      {status?.lastResetAt ? (
        <div className="muted" style={{ fontSize: '0.85rem' }}>
          Password updated manually on {formatDateTime(status.lastResetAt)}
          {status.lastResetByName ? ` by ${status.lastResetByName}` : ''}.
        </div>
      ) : null}
      {status ? (
        <div className="muted" style={{ fontSize: '0.85rem' }}>
          {status.usedThisMonth} of {status.limit} manual resets used this month
          {exhausted ? ` — available again ${formatDate(status.resetsOn)}` : ''}.
        </div>
      ) : null}

      {error ? <p className="alert alert--error">{error}</p> : null}

      {password ? (
        // Explicit flex column rather than `.stack`: `.stack` only adds margins
        // between children, so inline children (the label, the password, the
        // note) ran together on one line.
        <div
          className="alert alert--warn"
          style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: 0 }}
        >
          <div style={{ fontWeight: 600 }}>New password</div>
          <div
            aria-label="Generated password"
            style={{
              display: 'block',
              padding: '0.75rem 1rem',
              background: 'var(--surface)',
              color: 'var(--fg)',
              border: '1px solid var(--line-strong)',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '1.35rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textAlign: 'center',
              userSelect: 'all',
              wordBreak: 'break-all',
            }}
          >
            {password}
          </div>
          <div style={{ fontSize: '0.85rem' }}>
            Give this to the tenant now. It won&apos;t be shown again, and they&apos;ve been signed
            out everywhere.
          </div>
          <div className="row">
            <button type="button" className="button" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setPassword(null);
                setCopied(false);
              }}
            >
              Done
            </button>
          </div>
        </div>
      ) : confirming ? (
        <div className="alert alert--warn stack" style={{ gap: '0.5rem', marginBottom: 0 }}>
          <div style={{ fontSize: '0.9rem' }}>
            This replaces the tenant&apos;s password with a generated one and signs them out
            everywhere. It uses 1 of {status?.limit ?? 4} manual resets this month.
          </div>
          <div className="row">
            <button type="button" className="button" disabled={busy} onClick={() => void reset()}>
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
            <button
              type="button"
              className="button button--quiet"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="button button--quiet"
          disabled={status === null || exhausted}
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
        >
          Manually reset password
        </button>
      )}
    </div>
  );
}
